import { useMapViewRoutingMetadata } from "contexts/RoutingMetadataContext";
import { useGetLineTapCapacityCostsQuery } from "generated/graphql";
import React from "react";
import { isPricedConstraintConfig } from "types/componentConfigPerRegion";
import { LineTapConstraint } from "types/lineConstraintType";
import { Loading } from "types/loadingType";
import { ScopeName } from "types/scope";
import { ScopeViewType } from "types/scopeViewType";
import { FacilityId } from "types/selectedFacility";
import { CapacityCostsGraphRenderer } from "../shared/CostGraphsSection/CostGraphsSection";

type LineTapCapacityCostsGraphProps = {
    readonly tapFacilityId: FacilityId;
    readonly tapName: string;
    readonly scope: ScopeName;
    readonly scopeView: ScopeViewType;
    readonly includesPreExisting: boolean;
    readonly constraints: Loading<ReadonlyArray<LineTapConstraint>>;
    readonly projectEnergySize?: number;
    readonly projectChargingSize?: number;
};

/** Fetch a line tap's cost curve and render it with the shared cost graph. */
const LineTapCapacityCostsGraph: React.FC<LineTapCapacityCostsGraphProps> = ({
    tapFacilityId,
    tapName,
    scope,
    scopeView,
    includesPreExisting,
    constraints,
    projectEnergySize,
    projectChargingSize
}) => {
    const { componentConfig } = useMapViewRoutingMetadata();

    const { data, loading, error } = useGetLineTapCapacityCostsQuery({
        variables: {
            tapFacilityId,
            scope,
            scopeView,
            includesPreExisting
        }
    });

    // The renderer calls usePricedConstraintConfig, which throws for
    // curtailment regions.
    if (!isPricedConstraintConfig(componentConfig)) {
        return null;
    }

    const isLoading = loading || constraints === "loading";

    if (
        !isLoading &&
        (error || !data || data.line_capacity_costs.length === 0)
    ) {
        return null;
    }

    return (
        <CapacityCostsGraphRenderer
            exportId={tapFacilityId}
            exportName={tapName}
            scope={scope}
            scopeView={scopeView}
            capacityCostsData={
                !isLoading && data ? data.line_capacity_costs : []
            }
            constraints={constraints === "loading" ? [] : constraints}
            isLoading={isLoading}
            projectEnergySize={projectEnergySize}
            projectChargingSize={projectChargingSize}
        />
    );
};

export default LineTapCapacityCostsGraph;
